import { type LucideIcon } from 'lucide-react';

/**
 * What a single navigation destination is.
 *
 * The destinations themselves — and the workspaces they belong to — live in
 * navWorkspaces.ts. This file is deliberately data-free so the shape and the
 * permission predicate can be imported by the configuration without a cycle.
 *
 * `permission` renders the item only when the user holds it; `show` covers the
 * multi-permission gates. This is nav hygiene (P12A zero-exposure — stop
 * offering links that are guaranteed to 403), never a substitute for the
 * server check.
 */
export interface NavItem {
  /**
   * Where the entry goes, and its stable identity (keys, tests).
   *
   * For the ordinary case this is an in-app route. Two entries in the menu are
   * not routes at all and say so with `external`/`action` below — `to` is then
   * an absolute URL, or a `#`-prefixed id that no pathname can ever match.
   */
  to: string;
  icon: LucideIcon;
  label: string;
  permission?: string;
  show?: (can: (p: string) => boolean) => boolean;
  /**
   * `to` is an absolute URL that LEAVES DemoOps (My Tickets → the portal).
   * Renderers must use an <a>, not a router Link — a Link would produce a dead
   * in-app route — and must show that the link exits the app.
   */
  external?: boolean;
  /**
   * What a screen reader hears after an external label, e.g. "(opens the
   * NADC portal)". Defaults to the portal wording, which fits every external
   * entry but the preview site (2026-09-25).
   */
  hint?: string;
  /**
   * Not a destination: the entry runs something in place. 'guide' opens the
   * in-app Guide panel, which is mounted once in RootLayout and has no URL of
   * its own. Renderers must use a <button>.
   */
  action?: 'guide';
  /**
   * What the area's tab strip calls the page (2026-09-25 seven-destination
   * nav): "Inbox" under Equipment, where `label` — what the palette, /menu and
   * a pin need — stays "Equipment Inbox". Defaults to `label`.
   */
  tabLabel?: string;
  /**
   * Where an entry sits inside its area (2026-09-25). 'tab' (default) is a
   * page in the strip; 'action' is a thing you DO from the area — Job Blast
   * under Projects — rendered as a button at the strip's right, never a tab.
   */
  placement?: 'tab' | 'action';
  /** Extra words the ⌘K palette should match on — what people call the page. */
  keywords?: string;
  /**
   * Name of a count in useNavBadges() to show beside the label (e.g. how many
   * equipment requests are waiting). Zero renders nothing.
   */
  badge?: string;
  /**
   * One line of plain-language explanation of what the page is for. The rail
   * and the palette have no room for it and ignore it; /menu (the phone's
   * More screen) is a scannable list of cards, not icons, and shows it under
   * the label — the only place in the app description earns its space.
   */
  description?: string;
}

/** Does this person hold what the item needs? */
export function navItemVisible(item: NavItem, can: (p: string) => boolean): boolean {
  return item.show ? item.show(can) : !item.permission || can(item.permission);
}

/**
 * Is this entry an in-app route?
 *
 * Only route entries can be resolved against the current pathname or
 * navigated to with react-router. Asking here rather than at each of the four
 * render sites is what stops one of them from one day passing a portal URL to
 * `navigate()` and landing the person on a 404 inside DemoOps.
 */
export function isRouteItem(item: NavItem): boolean {
  return !item.external && !item.action;
}
